import CustomComponent from '../CustomComponent';

/**
 * Full-screen success / confirmation shell.
 * Pass the "What happens next?" (or any) block as light-DOM children.
 * Optional `redirect-url` navigates on Close (still fires `lta-success-screen:close`).
 *
 * @example
 * <lta-success-screen
 *   eyebrow="Congratulations"
 *   title="You've submitted a request to access this venue"
 *   description="This will be sent to the admin at your chosen venue."
 *   button-label="Close"
 *   redirect-url="/pages/venue-registration/landing-page/"
 * >
 *   <div class="u-bg-white u-rounded u-p-3">...</div>
 * </lta-success-screen>
 */
export class SuccessScreenComponent extends CustomComponent {
	static key = 'lta-success-screen';

	static get observedAttributes() {
		return [
			'eyebrow',
			'title',
			'description',
			'button-label',
			'icon',
			'icon-color',
			'classes',
			'hide-eyebrow',
			'hide-stripes',
			'redirect-url'
		];
	}

	render(childrenHTML) {
		const {
			eyebrow = 'Congratulations',
			title = '',
			description = '',
			buttonLabel = 'Close',
			icon = 'check-circle-no-fill',
			iconColor = '#6EB96F',
			classes = '',
			hideEyebrow = 'false',
			hideStripes = 'false'
		} = this.props;

		const slotContent = this._childrenHTML || childrenHTML || '';
		const showEyebrow = hideEyebrow !== 'true' && hideEyebrow !== '';
		const showStripes = hideStripes !== 'true' && hideStripes !== '';
		const classNames = ['lta-success-screen', classes].filter(Boolean).join(' ');

		return `
			<div class="${classNames} u-bg-background-green">
				<div class="lta-success-screen__inner l-grid l-lta-grid u-py-5 md:u-pt-10 u-pb-8">
					<div class="g-col-12 g-col-md-8 g-start-md-3 g-col-xl-6 g-start-xl-4">
						<div class="u-flex u-flex-column u-text-center">
								<div class="lta-success-screen__icon u-flex u-justify-center u-mb-2" aria-hidden="true">
									<lta-icon
										sprite-icon="${icon}"
										width="2.5rem"
										height="2.5rem"
										fill="${iconColor}"
									></lta-icon>
								</div>
								${
									showEyebrow && eyebrow
										? `<p class="u-label2 u-text-lta-dark-blue u-mb-1">${eyebrow}</p>`
										: ''
								}
								${
									title
										? `<h1 class="u-title2 u-mb-2">${title}</h1>`
										: ''
								}
								${
									description
										? `<p class="u-body1 u-text-grey-lta u-mb-4">${description}</p>`
										: ''
								}
								${slotContent ? `<div class="u-mb-4 u-text-left">${slotContent}</div>` : ''}
								<div class="u-flex u-justify-center">
									<button
										type="button"
										class="lta-btn lta-btn--lg lta-btn--primary lta-success-screen__button"
									>
										${buttonLabel}
									</button>
								</div>
							</div>
						</div>
				</div>
				${
					showStripes
						? `<div class="lta-success-screen__stripes" aria-hidden="true">
								<svg viewBox="0 0 217 58" class="lta-shape" xmlns="http://www.w3.org/2000/svg">
									<g fill="none" fill-rule="evenodd">
										<g fill="currentColor">
											<path d="M337.8 12530.31c-5.02-1.364-10.046-2.7-15.084-3.995-10.074-2.592-20.187-5.037-30.247-7.682-10.062-2.637-20.088-5.411-30.098-8.244l-7.508-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.027-2.771-15.043-4.145l-15.051-4.113c-5.017-1.371-10.04-2.716-15.067-4.053-5.027-1.333-10.057-2.657-15.092-3.961l-7.551-1.96-7.558-1.938-7.561-1.922-3.782-.957-3.785-.946c-.62-.156-1.241-.31-1.862-.465v.772l1.653.474 3.751 1.072 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.031 2.753 15.052 4.112 5.021 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.031 1.317 10.063 2.635 15.1 3.928l3.78.968 3.781.958 7.563 1.915c10.086 2.55 20.156 5.157 30.19 7.901 10.035 2.737 20.017 5.673 30.039 8.463 5.01 1.396 10.031 2.752 15.06 4.079 4.231 1.123 8.466 2.232 12.704 3.332h4.758l-2.05-.57c-5.012-1.39-10.028-2.764-15.049-4.12zm-182.8-52.66c1.594.46 3.189.917 4.784 1.373l3.754 1.072 3.756 1.062 7.516 2.115 7.52 2.098 7.525 2.078c5.017 1.387 10.039 2.754 15.063 4.114 5.025 1.354 10.053 2.7 15.087 4.02l15.103 3.962c5.036 1.317 10.07 2.636 15.112 3.93l3.782.968 3.784.959 7.569 1.916c10.093 2.55 20.17 5.159 30.211 7.904 10.043 2.738 20.033 5.675 30.061 8.466 5.014 1.396 10.039 2.753 15.071 4.08 5.031 1.335 10.066 2.652 15.106 3.954 4.281 1.106 8.566 2.202 12.855 3.279H372l-.846-.242c-5.004-1.433-10.016-2.836-15.032-4.226-5.016-1.39-10.036-2.765-15.06-4.123-5.023-1.364-10.053-2.7-15.094-3.996-10.082-2.593-20.203-5.039-30.27-7.685-10.07-2.639-20.102-5.414-30.12-8.248l-7.513-2.122-3.757-1.06-3.76-1.05c-5.013-1.4-10.034-2.773-15.053-4.148l-15.063-4.114c-5.02-1.372-10.048-2.718-15.078-4.055-5.03-1.333-10.064-2.658-15.103-3.963l-7.557-1.96-7.562-1.94-7.567-1.923-3.785-.957-3.787-.947c-1.664-.415-3.328-.829-4.993-1.241v.65zm164.443 52.549c-10.074-2.592-20.188-5.037-30.248-7.682-10.062-2.638-20.087-5.412-30.097-8.244l-7.508-2.121-3.754-1.06-3.758-1.05c-5.01-1.399-10.026-2.771-15.042-4.145l-15.051-4.113c-5.017-1.371-10.041-2.717-15.067-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.938-7.561-1.923-3.782-.956-2.374-.594v.891l2.131.61 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.699 15.077 4.018l15.092 3.96c5.031 1.317 10.062 2.635 15.1 3.928l3.78.968 3.78.958 7.564 1.915c10.086 2.55 20.156 5.157 30.19 7.9 10.035 2.738 20.017 5.674 30.038 8.464 4.32 1.203 8.649 2.376 12.983 3.527h5.417l-2.976-.807c-5.019-1.364-10.046-2.698-15.083-3.994zm-33.484-3.806c-10.062-2.638-20.087-5.412-30.097-8.245l-7.508-2.12-3.755-1.06-3.757-1.05c-5.01-1.399-10.026-2.772-15.042-4.146l-15.052-4.112c-5.016-1.372-10.04-2.717-15.066-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.939-7.561-1.922-2.92-.738v1l2.649.75 7.51 2.113 7.515 2.098 7.52 2.077c5.013 1.387 10.03 2.753 15.051 4.112 5.022 1.354 10.046 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.927l3.78.969 3.78.958 7.564 1.915c10.086 2.549 20.156 5.157 30.19 7.9 9.62 2.624 19.19 5.43 28.793 8.115h5.155c-1.189-.31-2.379-.62-3.569-.925-10.074-2.592-20.188-5.038-30.248-7.682zm-42.457 7.335l-7.468-2.11-3.734-1.055-3.737-1.044c-4.983-1.391-9.972-2.757-14.962-4.124l-14.97-4.09c-4.99-1.365-9.987-2.703-14.987-4.033-5-1.325-10.003-2.642-15.011-3.94l-7.511-1.948-6.122-1.57v1.325l5.757 1.608 7.48 2.066c4.986 1.38 9.977 2.738 14.97 4.09 4.996 1.346 9.993 2.685 14.997 3.997l15.011 3.939c5.005 1.31 10.01 2.62 15.02 3.906l3.759.963 3.761.952 7.524 1.904 1.718.436h3.017c-1.504-.423-3.008-.846-4.512-1.272zm-51.836-5.481c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.656-15.092-3.961l-6.507-1.688v1.48l6.111 1.688c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96 4.577 1.197h5.426c-3.205-.884-6.41-1.763-9.617-2.64l-15.05-4.113zm-20.774 2.776c-5.027-1.332-10.057-2.656-15.092-3.96l-.8-.208v1.576l.404.112c5.013 1.386 10.031 2.753 15.052 4.112 2.915.785 5.832 1.568 8.75 2.345h6.469c-4.923-1.345-9.852-2.665-14.783-3.977zm-2.92 3.42c-4.322-1.144-8.645-2.283-12.972-3.409v1.622c2.856.786 5.713 1.567 8.572 2.344h6.487l-2.087-.557zm20.872-2.813c-5.017-1.371-10.041-2.716-15.067-4.053-5.028-1.332-10.057-2.657-15.092-3.961l-3.685-.956v1.528l3.289.908c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.7 15.077 4.018l6.758 1.774h6l-12.331-3.37zm63.716-9.594l-7.508-2.12-3.754-1.061-3.757-1.05c-5.01-1.398-10.026-2.77-15.043-4.145l-15.05-4.112c-5.018-1.372-10.042-2.717-15.068-4.054-5.027-1.332-10.057-2.657-15.092-3.961l-7.551-1.96-7.557-1.938-7.18-1.825v1.098l6.858 1.93 7.514 2.098 7.52 2.077c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.1 3.927l3.78.969 3.781.958 7.564 1.915c10.085 2.549 20.155 5.157 30.189 7.9 5.035 1.374 10.057 2.797 15.077 4.227h3.597c-6.187-1.551-12.375-3.098-18.542-4.72-10.062-2.637-20.088-5.411-30.098-8.244zm26.812 12.132c-10.062-2.637-20.088-5.411-30.097-8.244l-7.509-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.026-2.77-15.042-4.145l-15.052-4.112c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.657-15.091-3.961l-7.552-1.96-7.557-1.938-3.894-.99v1.188l3.572 1.005 7.514 2.098 7.52 2.077c5.013 1.387 10.032 2.753 15.052 4.112 5.022 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.928l3.779.968 3.781.958 7.564 1.916c10.085 2.549 20.155 5.156 30.189 7.9l1.231.339h2.093c-1.064-.276-2.129-.552-3.192-.832zm-123.04-27.322l7.479 2.089 7.484 2.068c4.99 1.38 9.984 2.74 14.98 4.092 4.998 1.347 9.998 2.687 15.006 4l15.02 3.94c5.008 1.311 10.016 2.623 15.03 3.91l3.761.963 3.764.953 7.528 1.906c5.497 1.389 10.988 2.796 16.472 4.233h1.979c-6.085-1.657-12.16-3.352-18.227-5.07l-7.473-2.111-3.737-1.056-3.739-1.045c-4.986-1.392-9.979-2.758-14.971-4.126l-14.98-4.093c-4.994-1.365-9.994-2.704-14.996-4.035-5.004-1.326-10.01-2.644-15.02-3.942l-7.517-1.95-7.522-1.928-1.653-.42v1.248l1.332.374zm-1.332 8.74v-1.418l2.01.516 7.552 1.959c5.035 1.305 10.064 2.629 15.092 3.96 5.026 1.338 10.05 2.683 15.067 4.055l15.051 4.112c5.016 1.374 10.033 2.747 15.043 4.145l3.757 1.05 3.664 1.035h-4.399l-3.35-.859c-5.038-1.292-10.07-2.61-15.101-3.927l-15.092-3.96c-5.031-1.32-10.055-2.665-15.077-4.019-5.02-1.359-10.038-2.725-15.051-4.112l-7.52-2.077-1.646-.46z" transform="translate(-155 -12477)"/>
										</g>
									</g>
								</svg>
							</div>`
						: ''
				}
			</div>
		`;
	}

	afterRender() {
		const button = this.querySelector('.lta-success-screen__button');
		if (!button) {
			return;
		}

		button.addEventListener('click', () => {
			this.dispatchEvent(
				new CustomEvent('lta-success-screen:close', {
					bubbles: true,
					composed: true
				})
			);

			const redirectUrl = this.props.redirectUrl;
			if (redirectUrl) {
				window.location.href = redirectUrl;
			}
		});
	}
}

export default SuccessScreenComponent;
